import React, { useId } from 'react';
import { PressableProps, StyleProp, ViewProps, ViewStyle } from 'react-native';
import { PressableStateCallbackType } from 'react-native';
import Animated, { FadeIn, FadeOut } from 'react-native-reanimated';
import { styles } from './PressableHovered.style';
import TextLato from '@photosi/rn-components/text/text-lato/TextLato';
import { BaseButton } from 'src/components/buttons/base-button/BaseButton';
import { TBaseButtonChildrenProps } from 'src/components/buttons/base-button/BaseButton.types';
import { FocusGroups } from '@photosi/rn-components/focus-provider/FocusProvider.constants';

type HoverablePressableState = PressableStateCallbackType & {
  hovered?: boolean;
};

type Props = {
  hint?: string;
  style?: StyleProp<ViewStyle> | ((state: HoverablePressableState) => StyleProp<ViewStyle>);
  innerStyle?: PressableProps['style'];
  onPress?: PressableProps['onPress'];
  onKeyDown?: (event: any) => void;
  onLayout?: PressableProps['onLayout'];
  children: React.ReactNode | ((state: HoverablePressableState) => React.ReactNode);
  hintStyle?: ViewProps['style'];
  disabled?: boolean;
  pressed?: boolean;
  testID?: string;
  accessibilityLabel?: string;
  accessibilityRole?: PressableProps['accessibilityRole'];
  accessibilityState?: PressableProps['accessibilityState'];
  accessible?: boolean;
  focusGroup?: FocusGroups;
  focusOrder?: number;
  focusStyle?: PressableProps['style'];
  focusId?: string;
};

export const PressableHovered = ({
  hint,
  style,
  innerStyle,
  onPress,
  onKeyDown,
  onLayout,
  children,
  hintStyle = {},
  disabled = false,
  pressed = false,
  testID,
  accessibilityLabel,
  accessibilityRole,
  accessibilityState,
  accessible = true,
  focusGroup = FocusGroups.PRODUCT,
  focusOrder = 0,
  focusStyle = styles.focusRing,
  focusId,
}: Props) => {
  const generatedId = useId();
  const id = focusId ?? generatedId;
  const isFocusable = accessible !== false && !disabled;

  // Resolve style for BaseButton's static style props.
  // If style is a function, compute base, hover, and pressed variants by calling it with different states.
  const baseStyle: StyleProp<ViewStyle> =
    typeof style === 'function' ? style({ pressed: false, hovered: false }) : style;
  const hoverStyle: StyleProp<ViewStyle> =
    typeof style === 'function' ? style({ pressed: false, hovered: true }) : undefined;
  const pressedStyle: StyleProp<ViewStyle> =
    typeof style === 'function' ? style({ pressed: true, hovered: false }) : undefined;

  // Wrap onPress to be async (required by BaseButton's type)
  const wrappedOnPress = onPress ? async (e: any) => (onPress as any)(e) : undefined;

  return (
    <BaseButton
      focusId={id}
      focusOrder={focusOrder}
      focusGroup={focusGroup}
      style={baseStyle}
      hoverStyle={hoverStyle}
      pressedStyle={pressedStyle}
      focusStyle={focusStyle}
      innerStyle={innerStyle}
      onLayout={onLayout}
      onPress={wrappedOnPress}
      onKeyDown={onKeyDown as any}
      disabled={disabled}
      focusable={isFocusable}
      testID={testID}
      accessible={accessible}
      accessibilityLabel={accessibilityLabel ?? hint}
      accessibilityRole={accessibilityRole ?? 'button'}
      accessibilityState={accessibilityState}
    >
      {({ hovered, pressed: pressedState }: TBaseButtonChildrenProps) => (
        <>
          {typeof children === 'function' ? children({ pressed: pressedState, hovered }) : children}
          {hint && hovered && !pressed && (
            <Animated.View
              entering={FadeIn.duration(200)}
              exiting={FadeOut.duration(200)}
              style={[styles.hoverTextContainer, hintStyle]}
              pointerEvents="none"
            >
              <TextLato style={styles.hoverText}>{hint}</TextLato>
            </Animated.View>
          )}
        </>
      )}
    </BaseButton>
  );
};
